import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  FileText, 
  Plane, 
  Globe,
  Camera,
  Download,
  ChevronRight,
  Shield,
  Star,
  PawPrint,
  FileCheck,
  Eye
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const SamplePDFPreview = ({ onCtaClick }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('overview');

  // Sample data for the preview - translated
  const sampleData = {
    pet: {
      name: 'Luna',
      type: t('sample_report.pet_type'),
      breed: 'Golden Retriever',
      weight: '28 kg'
    },
    flight: {
      airline: 'Air France',
      airlineCode: 'AF',
      origin: 'Paris (CDG)',
      destination: 'New York (JFK)',
      date: t('sample_report.flight_date')
    },
    verdict: {
      status: 'OK',
      confidence: t('sample_report.confidence_high'),
      summary: t('sample_report.verdict_summary')
    },
    crateChecks: [
      { name: t('sample_report.check_size'), pass: true, notes: t('sample_report.check_size_notes') },
      { name: t('sample_report.check_ventilation'), pass: true, notes: t('sample_report.check_ventilation_notes') },
      { name: t('sample_report.check_fasteners'), pass: false, warn: true, notes: t('sample_report.check_fasteners_notes') },
      { name: t('sample_report.check_food_water'), pass: true, notes: t('sample_report.check_food_water_notes') },
      { name: t('sample_report.check_labeling'), pass: true, notes: t('sample_report.check_labeling_notes') }
    ],
    docChecks: [
      { name: t('sample_report.doc_health_cert'), pass: true, notes: t('sample_report.doc_health_cert_notes') },
      { name: t('sample_report.doc_vaccination'), pass: true, notes: t('sample_report.doc_vaccination_notes') },
      { name: t('sample_report.doc_microchip'), pass: true, notes: t('sample_report.doc_microchip_notes') },
      { name: t('sample_report.doc_import_permit'), pass: false, notes: t('sample_report.doc_import_permit_notes') }
    ],
    airlineRules: [
      { category: t('sample_report.category_crate'), rule: t('sample_report.airline_rule_1') },
      { category: t('sample_report.category_crate'), rule: t('sample_report.airline_rule_2') },
      { category: t('sample_report.category_documents'), rule: t('sample_report.airline_rule_3') },
      { category: t('sample_report.category_restrictions'), rule: t('sample_report.airline_rule_4') }
    ],
    countryRules: {
      name: t('sample_report.country_usa'),
      quarantine: false,
      importPermit: false,
      bloodTest: false,
      requirements: [
        t('sample_report.country_req_1'),
        t('sample_report.country_req_2'),
        t('sample_report.country_req_3')
      ]
    }
  };

  const sections = [
    { id: 'overview', label: t('sample_report.tab_verdict'), icon: Shield },
    { id: 'crate', label: t('sample_report.tab_crate'), icon: Camera },
    { id: 'documents', label: t('sample_report.tab_documents'), icon: FileText },
    { id: 'airline', label: t('sample_report.tab_airline'), icon: Plane },
    { id: 'country', label: t('sample_report.tab_country'), icon: Globe }
  ];

  return (
    <section data-testid="sample-pdf-section" className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Eye className="w-4 h-4" />
            {t('sample_report.preview_badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {t('sample_report.title')}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('sample_report.subtitle')}
          </p>
          <p data-testid="sample-fictitious-note" className="text-sm text-amber-700 mt-2">{t('disclaimer.sample')}</p>
        </div>

        {/* Main Preview Container */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
          {/* Premium Badge */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white py-3 px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="PetFlyCheck" className="w-10 h-10 object-contain bg-white rounded-lg p-1" />
                <div>
                  <p className="font-bold text-lg">{t('sample_report.premium_title')}</p>
                  <p className="text-blue-200 text-sm">{t('sample_report.premium_subtitle')}</p>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
                <span className="text-sm font-medium">{t('sample_report.ultra_premium')}</span>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-slate-200 bg-slate-50">
            <div className="flex overflow-x-auto">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeSection === section.id
                      ? 'border-blue-600 text-blue-600 bg-white'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6 md:p-8">
            {/* Overview Section */}
            {activeSection === 'overview' && (
              <div className="space-y-6">
                {/* Pet & Flight Info Banner */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center">
                        <PawPrint className="w-8 h-8 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-amber-700 font-medium">{t('sample_report.pet_info')}</p>
                        <p className="text-xl font-bold text-slate-900">{sampleData.pet.name}</p>
                        <p className="text-sm text-slate-600">{sampleData.pet.breed} • {sampleData.pet.weight}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl p-5 border border-blue-200">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Plane className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-700 font-medium">{t('sample_report.flight_info')}</p>
                        <p className="text-xl font-bold text-slate-900">{sampleData.flight.airline}</p>
                        <p className="text-sm text-slate-600">{sampleData.flight.origin} → {sampleData.flight.destination}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Verdict Status */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white" data-testid="sample-verdict">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <AlertTriangle className="w-12 h-12" />
                    <div>
                      <h3 className="text-3xl font-bold">{t('sample_report.verdict_compliant')}</h3>
                      <p className="text-amber-50">{t('sample_report.confidence')}: {sampleData.verdict.confidence}</p>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-blue-600" />
                    {t('sample_report.analysis_summary')}
                  </h4>
                  <p className="text-slate-700 leading-relaxed">{sampleData.verdict.summary}</p>
                </div>

                {/* Photo Preview */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Camera className="w-5 h-5 text-blue-600" />
                    {t('sample_report.analyzed_photos')}
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="aspect-square bg-gradient-to-br from-slate-200 to-slate-300 rounded-lg flex items-center justify-center relative overflow-hidden">
                      <img 
                        src="https://static.prod-images.emergentagent.com/jobs/64897331-93bb-49a9-9089-bc45b7319e45/images/7099a3a85c2af136c49561a0b39577748ed03db1302e66e1c4588328f72ff300.png" 
                        alt="Pet inside crate" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center">
                        {t('sample_report.photo_pet_crate')}
                      </div>
                    </div>
                    <div className="aspect-square bg-gradient-to-br from-slate-200 to-slate-300 rounded-lg flex items-center justify-center relative overflow-hidden">
                      <img 
                        src="https://static.prod-images.emergentagent.com/jobs/64897331-93bb-49a9-9089-bc45b7319e45/images/9ebb7f37331428672c07803d317b133529f93bc8a77ba6186f5ce3154217a3bf.png" 
                        alt="Full crate view" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center">
                        {t('sample_report.photo_full_crate')}
                      </div>
                    </div>
                    <div className="aspect-square bg-gradient-to-br from-slate-200 to-slate-300 rounded-lg flex items-center justify-center relative overflow-hidden">
                      <img 
                        src="https://static.prod-images.emergentagent.com/jobs/64897331-93bb-49a9-9089-bc45b7319e45/images/63bde2e9a53e937b89822f146f3a5f7f6a6c43bff6b85840a034360da19ea61c.png" 
                        alt="Door and security" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-1 px-2 text-center">
                        {t('sample_report.photo_door_security')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Crate Analysis Section */}
            {activeSection === 'crate' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Camera className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{t('sample_report.crate_title')}</h3>
                    <p className="text-sm text-slate-500">{t('sample_report.crate_subtitle')}</p>
                  </div>
                </div>

                {sampleData.crateChecks.map((check, idx) => {
                  const tone = check.pass ? 'green' : check.warn ? 'amber' : 'red';
                  const Icon = check.pass ? CheckCircle2 : check.warn ? AlertTriangle : XCircle;
                  const styles = {
                    green: ['bg-green-50 border-green-200', 'text-green-600', 'text-green-800', 'bg-green-200 text-green-800'],
                    amber: ['bg-amber-50 border-amber-300', 'text-amber-600', 'text-amber-800', 'bg-amber-200 text-amber-800'],
                    red: ['bg-red-50 border-red-200', 'text-red-600', 'text-red-800', 'bg-red-200 text-red-800'],
                  }[tone];
                  return (
                    <div key={idx} data-testid={`sample-crate-check-${idx}`} className={`flex items-start gap-4 p-4 rounded-xl border ${styles[0]}`}>
                      <Icon className={`w-6 h-6 ${styles[1]} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <p className={`font-semibold ${styles[2]}`}>{check.name}</p>
                        <p className="text-sm text-slate-600 mt-1">{check.notes}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[3]}`}>
                        {check.pass ? t('sample_report.pass') : check.warn ? t('sample_report.fix') : t('sample_report.fail')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Documents Section */}
            {activeSection === 'documents' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{t('sample_report.docs_title')}</h3>
                    <p className="text-sm text-slate-500">{t('sample_report.docs_subtitle')}</p>
                  </div>
                </div>

                {/* Document thumbnails */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[t('sample_report.doc_health_cert'), t('sample_report.doc_vaccination'), t('sample_report.doc_microchip'), t('sample_report.doc_passport')].map((doc, idx) => (
                    <div key={idx} className="bg-slate-100 rounded-lg p-3 text-center">
                      <div className="w-full aspect-[3/4] bg-white rounded border-2 border-dashed border-slate-300 flex items-center justify-center mb-2">
                        <FileText className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-xs font-medium text-slate-600">{doc}</p>
                    </div>
                  ))}
                </div>

                {sampleData.docChecks.map((check, idx) => (
                  <div key={idx} className={`flex items-start gap-4 p-4 rounded-xl border ${check.pass ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    {check.pass ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold ${check.pass ? 'text-green-800' : 'text-amber-800'}`}>{check.name}</p>
                      <p className="text-sm text-slate-600 mt-1">{check.notes}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Airline Rules Section */}
            {activeSection === 'airline' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Plane className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Air France - {t('sample_report.iata_rules')}</h3>
                    <p className="text-sm text-slate-500">{t('sample_report.airline_subtitle')}</p>
                  </div>
                </div>

                {[t('sample_report.category_crate'), t('sample_report.category_documents'), t('sample_report.category_restrictions')].map((category) => {
                  const rules = sampleData.airlineRules.filter(r => r.category === category);
                  if (rules.length === 0) return null;
                  return (
                    <div key={category} className="bg-slate-50 rounded-xl p-5">
                      <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                        {category === t('sample_report.category_crate') && <Camera className="w-4 h-4" />}
                        {category === t('sample_report.category_documents') && <FileText className="w-4 h-4" />}
                        {category === t('sample_report.category_restrictions') && <AlertTriangle className="w-4 h-4" />}
                        {category}
                      </h4>
                      <ul className="space-y-2">
                        {rules.map((rule, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <ChevronRight className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            {rule.rule}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Country Rules Section */}
            {activeSection === 'country' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Globe className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{sampleData.countryRules.name} - {t('sample_report.import_requirements')}</h3>
                    <p className="text-sm text-slate-500">{t('sample_report.country_subtitle')}</p>
                  </div>
                </div>

                {/* Quick Facts */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="bg-green-50 rounded-xl p-3 sm:p-4 text-center border border-green-200">
                    <p className="text-xs sm:text-sm text-green-700">{t('sample_report.quarantine')}</p>
                    <p className="text-sm sm:text-lg font-bold text-green-800 leading-tight">{t('sample_report.not_required')}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 sm:p-4 text-center border border-green-200">
                    <p className="text-xs sm:text-sm text-green-700">{t('sample_report.import_permit')}</p>
                    <p className="text-sm sm:text-lg font-bold text-green-800 leading-tight">{t('sample_report.not_required')}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 sm:p-4 text-center border border-green-200">
                    <p className="text-xs sm:text-sm text-green-700">{t('sample_report.blood_test')}</p>
                    <p className="text-sm sm:text-lg font-bold text-green-800 leading-tight">{t('sample_report.not_required')}</p>
                  </div>
                </div>

                {/* Requirements List */}
                <div className="bg-slate-50 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 mb-4">{t('sample_report.entry_requirements')}</h4>
                  <ul className="space-y-3">
                    {sampleData.countryRules.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Shield className="w-4 h-4" />
                <span>{t('sample_report.report_id')}: SAMPLE-2026-DEMO</span>
                <span className="mx-2">•</span>
                <span>{t('sample_report.generated')}: {new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" className="gap-2" disabled>
                  <Download className="w-4 h-4" />
                  {t('sample_report.download_pdf')}
                </Button>
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700 gap-2 cursor-pointer"
                  onClick={onCtaClick}
                  data-testid="sample-report-cta"
                >
                  {t('sample_report.get_your_report')}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Camera className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">{t('sample_report.feature_photo_title')}</h3>
            <p className="text-sm text-slate-600">{t('sample_report.feature_photo_desc')}</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-7 h-7 text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">{t('sample_report.feature_doc_title')}</h3>
            <p className="text-sm text-slate-600">{t('sample_report.feature_doc_desc')}</p>
          </div>
          <div className="text-center">
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Globe className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">{t('sample_report.feature_country_title')}</h3>
            <p className="text-sm text-slate-600">{t('sample_report.feature_country_desc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SamplePDFPreview;
